import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getAllTenants, toggleTenant, resendTenantSetupLink, getTenantAiConfigs, updateTenantAiConfig, getTenantBilling, updateTenantPlan, getPlatformAuditLog, getPlatformLinkedInConfig, savePlatformLinkedInConfig, logManualPayment, getBillingAnomalies, resolveBillingAnomaly, getBillingTrends, updateTenantModules } from "../api";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";
import { AsyncButton } from "../components/AsyncButton";
import { DateInput } from "../components/DateInput";
import { formatDate, formatDateTime } from "../utils/dateFormat";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { MODULE_KEYS, MODULE_LABELS, MODULE_COLORS, ModuleKey, ModuleMap } from "../moduleMetadata";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:30801";
const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";

interface Tenant {
  id: number; name: string; domain: string; contact_email: string;
  is_active: boolean; created_at: string;
  stats: { users: number; jobs: number; candidates: number };
  modules?: ModuleMap;
}

interface RevokeWarning {
  module: string; module_label: string; roles: string[];
  user_count: number; users: { id: number; email: string; role: string }[];
  message: string;
}

async function apiFetch(path: string, opts?: RequestInit) {
  return fetch(`${API_URL}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
}

// ── Module entitlements ───────────────────────────────────────────────────────

/** Read-only strip of which modules a tenant owns. */
function ModuleStrip({ modules }: { modules?: ModuleMap }) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
      {MODULE_KEYS.map(key => {
        const owned = modules?.[key] === true;
        return (
          <span
            key={key}
            title={owned ? `${MODULE_LABELS[key]} — owned` : `${MODULE_LABELS[key]} — not on plan`}
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              fontSize: 11, fontWeight: 600, borderRadius: 999,
              padding: "3px 9px",
              background: owned ? "#f8fafc" : "transparent",
              border: `1px solid ${owned ? MODULE_COLORS[key] + "55" : "#e2e8f0"}`,
              color: owned ? "#0f172a" : "#94a3b8",
            }}
          >
            <span style={{
              width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
              background: owned ? MODULE_COLORS[key] : "#cbd5e1",
            }} />
            {MODULE_LABELS[key]}
          </span>
        );
      })}
    </div>
  );
}

/**
 * Grant/revoke editor for one tenant.
 *
 * Sends only the delta, so an unchanged module is never touched. Grants and
 * revokes are DB-only — nothing here calls Stripe. When a revoke strands users
 * whose role depends on the module, the backend returns advisory warnings which
 * are surfaced here rather than swallowed.
 */
function ModuleEditor({ tenant, onClose }: { tenant: Tenant; onClose: () => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [draft, setDraft] = useState<Record<string, boolean>>(
    () => Object.fromEntries(MODULE_KEYS.map(k => [k, tenant.modules?.[k] === true])),
  );
  const [warnings, setWarnings] = useState<RevokeWarning[]>([]);

  const original = (k: ModuleKey) => tenant.modules?.[k] === true;
  const grant = MODULE_KEYS.filter(k => draft[k] && !original(k));
  const revoke = MODULE_KEYS.filter(k => !draft[k] && original(k));
  const dirty = grant.length > 0 || revoke.length > 0;

  const saveMut = useMutation({
    mutationFn: () => updateTenantModules(tenant.id, { grant, revoke }),
    onSuccess: (d: any) => {
      qc.invalidateQueries({ queryKey: ["all-tenants"] });
      setWarnings(d?.warnings ?? []);
      const parts = [
        d?.granted?.length ? `granted ${d.granted.length}` : "",
        d?.revoked?.length ? `revoked ${d.revoked.length}` : "",
      ].filter(Boolean).join(", ");
      toast(`Modules updated — ${parts || "no change"}`, "success");
      if (!d?.warnings?.length) onClose();
    },
    onError: (e: any) =>
      toast(e?.response?.data?.detail?.message || "Could not update modules", "error"),
  });

  return (
    <div style={{
      marginTop: 14, paddingTop: 14, borderTop: "1px solid #e2e8f0",
      display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", letterSpacing: ".06em", textTransform: "uppercase" }}>
        Modules for {tenant.name}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 7 }}>
        {MODULE_KEYS.map(key => {
          const on = draft[key];
          return (
            <label key={key} style={{
              display: "flex", alignItems: "center", gap: 9, cursor: "pointer",
              border: `1px solid ${on ? MODULE_COLORS[key] + "66" : "#e2e8f0"}`,
              background: on ? "#f8fafc" : "white",
              borderRadius: 8, padding: "9px 11px", fontSize: 13, minHeight: 44,
            }}>
              <input
                type="checkbox"
                checked={on}
                onChange={e => setDraft(d => ({ ...d, [key]: e.target.checked }))}
                style={{ width: 15, height: 15, accentColor: NAVY, cursor: "pointer" }}
              />
              <span style={{
                width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                background: on ? MODULE_COLORS[key] : "#cbd5e1",
              }} />
              <span style={{ color: "#0f172a", fontWeight: on ? 650 : 500 }}>{MODULE_LABELS[key]}</span>
            </label>
          );
        })}
      </div>

      <div style={{ fontSize: 12, color: "#64748b" }}>
        People is always included. Changes here write entitlements directly and
        do not touch Stripe — billing is a separate, manual step.
      </div>

      {dirty && (
        <div style={{ fontSize: 12, color: "#334155" }}>
          {grant.length > 0 && <>Granting <b>{grant.map(k => MODULE_LABELS[k]).join(", ")}</b>. </>}
          {revoke.length > 0 && <>Revoking <b>{revoke.map(k => MODULE_LABELS[k]).join(", ")}</b>.</>}
        </div>
      )}

      {warnings.length > 0 && (
        <div style={{
          border: "1px solid #fcd34d", background: "#fffbeb",
          borderRadius: 8, padding: "11px 13px", fontSize: 12.5, color: "#78350f",
        }}>
          <div style={{ fontWeight: 700, marginBottom: 5 }}>Saved — but check these users</div>
          {warnings.map(w => (
            <div key={w.module} style={{ marginBottom: 5 }}>
              {w.message}
              <div style={{ color: "#92400e", marginTop: 2 }}>
                {w.users.map(u => `${u.email} (${u.role})`).join(", ")}
              </div>
            </div>
          ))}
          <button onClick={onClose} style={{
            marginTop: 6, background: "white", border: "1px solid #cbd5e1",
            color: "#334155", padding: "6px 13px", borderRadius: 6,
            fontSize: 12.5, fontWeight: 600, cursor: "pointer", minHeight: 44,
          }}>Dismiss</button>
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <AsyncButton
          onClick={() => saveMut.mutateAsync()}
          disabled={!dirty}
          style={{
            background: dirty ? NAVY : "#e2e8f0", color: dirty ? "white" : "#94a3b8",
            border: "none", padding: "9px 17px", borderRadius: 6,
            fontWeight: 600, fontSize: 13, cursor: dirty ? "pointer" : "not-allowed",
            minHeight: 44,
          }}
        >
          Save modules
        </AsyncButton>
        <button onClick={onClose} style={{
          background: "white", border: "1px solid #cbd5e1", color: "#334155",
          padding: "9px 17px", borderRadius: 6, fontWeight: 600, fontSize: 13,
          cursor: "pointer", minHeight: 44,
        }}>Cancel</button>
      </div>
    </div>
  );
}

// ── Tenants tab ───────────────────────────────────────────────────────────────

function TenantsTab({ onEnterTenant }: { onEnterTenant: (t: { id: number; name: string }) => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [linkCopiedId, setLinkCopiedId] = useState<number | null>(null);
  const [modulesOpenId, setModulesOpenId] = useState<number | null>(null);
  const { data: tenants = [], isLoading } = useQuery<Tenant[]>({
    queryKey: ["all-tenants"], queryFn: getAllTenants,
  });
  const toggleMut = useMutation({
    mutationFn: toggleTenant,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["all-tenants"] }),
  });
  const resendLinkMut = useMutation({
    mutationFn: resendTenantSetupLink,
    onSuccess: async (d, id) => {
      await navigator.clipboard.writeText(d.setup_link).catch(() => {});
      setLinkCopiedId(id);
      toast("Setup link regenerated and copied", "success");
      setTimeout(() => setLinkCopiedId(null), 4000);
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to regenerate link", "error"),
  });

  if (isLoading) return <p style={{ color: "#64748b" }}>Loading…</p>;

  return (
    <>
      <LoadingOverlay active={toggleMut.isPending || resendLinkMut.isPending} />
      <p style={{ color: "#64748b", fontSize: 14, margin: "0 0 20px" }}>
        {tenants.length} workspace{tenants.length !== 1 ? "s" : ""} on the platform
      </p>
      <div style={{ display: "grid", gap: 12 }}>
        {tenants.map(t => (
          <div key={t.id} style={{
            background: "white", border: "1px solid #e2e8f0", borderRadius: 12,
            padding: "18px 22px", display: "flex", alignItems: "flex-start",
            justifyContent: "space-between", gap: 16, opacity: t.is_active ? 1 : 0.6,
            flexWrap: "wrap",
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                <span style={{ fontWeight: 700, fontSize: 16, color: "#1e293b" }}>{t.name}</span>
                <span style={{
                  background: t.is_active ? "#dcfce7" : "#fee2e2",
                  color: t.is_active ? "#166534" : "#991b1b",
                  border: `1px solid ${t.is_active ? "#86efac" : "#fca5a5"}`,
                  padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                }}>{t.is_active ? "Active" : "Disabled"}</span>
              </div>
              <div style={{ fontSize: 13, color: "#64748b", display: "flex", gap: 16, flexWrap: "wrap" }}>
                {t.domain && <span>{t.domain}</span>}
                {t.contact_email && <span>{t.contact_email}</span>}
                <span>Created {formatDate(t.created_at)}</span>
              </div>
              <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
                {[["users", t.stats.users], ["jobs", t.stats.jobs], ["candidates", t.stats.candidates]].map(([label, val]) => (
                  <div key={label as string} style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: NAVY }}>{val}</div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>{label}</div>
                  </div>
                ))}
              </div>
              <ModuleStrip modules={t.modules} />
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <button onClick={() => onEnterTenant({ id: t.id, name: t.name })} style={{
                background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`, color: "white",
                border: "none", padding: "8px 16px", borderRadius: 7, cursor: "pointer",
                fontWeight: 600, fontSize: 13,
              }}>Enter</button>
              <button
                onClick={() => resendLinkMut.mutate(t.id)}
                disabled={resendLinkMut.isPending && resendLinkMut.variables === t.id}
                style={{
                  background: linkCopiedId === t.id ? "#f0fdf4" : "none",
                  border: `1px solid ${linkCopiedId === t.id ? "#86efac" : "#e2e8f0"}`,
                  color: linkCopiedId === t.id ? "#16a34a" : "#475569",
                  padding: "8px 14px", borderRadius: 7,
                  cursor: resendLinkMut.isPending && resendLinkMut.variables === t.id ? "wait" : "pointer",
                  fontSize: 13, fontWeight: 600,
                }}
              >
                {resendLinkMut.isPending && resendLinkMut.variables === t.id
                  ? "Generating…"
                  : linkCopiedId === t.id ? "✓ Link copied!" : "🔄 Regenerate link"}
              </button>
              <button
                onClick={() => setModulesOpenId(modulesOpenId === t.id ? null : t.id)}
                aria-expanded={modulesOpenId === t.id}
                style={{
                  background: modulesOpenId === t.id ? "#eff6ff" : "none",
                  border: `1px solid ${modulesOpenId === t.id ? "#93c5fd" : "#e2e8f0"}`,
                  color: modulesOpenId === t.id ? NAVY : "#475569",
                  padding: "8px 14px", borderRadius: 7, cursor: "pointer",
                  fontSize: 13, fontWeight: 600,
                }}
              >
                {modulesOpenId === t.id ? "Close modules" : "Modules"}
              </button>
              <button onClick={() => toggleMut.mutate(t.id)} disabled={toggleMut.isPending} style={{
                background: t.is_active ? "#fff5f5" : "#f0fdf4",
                color: t.is_active ? "#dc2626" : "#16a34a",
                border: `1px solid ${t.is_active ? "#fca5a5" : "#86efac"}`,
                padding: "8px 14px", borderRadius: 7, cursor: "pointer", fontSize: 13,
              }}>{t.is_active ? "Disable" : "Enable"}</button>
            </div>
            {modulesOpenId === t.id && (
              <div style={{ flexBasis: "100%" }}>
                <ModuleEditor tenant={t} onClose={() => setModulesOpenId(null)} />
              </div>
            )}
          </div>
        ))}
      </div>
      {tenants.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
          <div style={{ fontSize: 40 }}>🏢</div>
          <div style={{ marginTop: 10 }}>No tenants yet.</div>
        </div>
      )}
    </>
  );
}

// ── Requests tab ──────────────────────────────────────────────────────────────

function RequestsTab() {
  const [filter, setFilter] = useState<"pending" | "approved" | "rejected">("pending");
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [regenId, setRegenId] = useState<number | null>(null);
  const [regenLoading, setRegenLoading] = useState<number | null>(null);
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();

  const { data: requests = [], isLoading } = useQuery<any[]>({
    queryKey: ["reg-requests", filter],
    queryFn: async () => {
      const r = await apiFetch(`/api/admin/tenants/requests?status=${filter}`);
      return r.json();
    },
    refetchInterval: filter === "pending" ? 30_000 : false,
  });

  async function approve(id: number) {
    const r = await apiFetch(`/api/admin/tenants/requests/${id}/approve`, { method: "POST" });
    const d = await r.json();
    if (!r.ok) { toast(d.detail || "Error", "error"); return; }
    await navigator.clipboard.writeText(d.setup_link).catch(() => {});
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 4000);
    qc.invalidateQueries({ queryKey: ["reg-requests"] });
    qc.invalidateQueries({ queryKey: ["all-tenants"] });
  }

  async function reject(id: number, companyName: string) {
    if (!await confirm(`Reject the access request from ${companyName}? No workspace will be created for them.`,
      { title: "Reject request", confirmLabel: "Reject" })) return;
    const r = await apiFetch(`/api/admin/tenants/requests/${id}/reject`, { method: "POST" });
    if (r.ok) qc.invalidateQueries({ queryKey: ["reg-requests"] });
  }

  async function copyLink(url: string, id: number) {
    await navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 3000);
  }

  async function regenerateLink(id: number) {
    setRegenLoading(id);
    try {
      const r = await apiFetch(`/api/admin/tenants/requests/${id}/resend-link`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) { toast(d.detail || "Error", "error"); return; }
      await navigator.clipboard.writeText(d.setup_link).catch(() => {});
      setRegenId(id);
      setCopiedId(id);
      setTimeout(() => { setRegenId(null); setCopiedId(null); }, 4000);
      qc.invalidateQueries({ queryKey: ["reg-requests"] });
    } finally {
      setRegenLoading(null);
    }
  }

  return (
    <>
      <LoadingOverlay active={regenLoading !== null} />
      <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
        {(["pending", "approved", "rejected"] as const).map(s => (
          <button key={s} onClick={() => setFilter(s)} style={{
            padding: "6px 16px", borderRadius: 20, border: "1.5px solid",
            borderColor: filter === s ? NAVY : "#e2e8f0",
            background: filter === s ? NAVY : "white",
            color: filter === s ? "white" : "#64748b",
            fontWeight: 600, fontSize: 13, cursor: "pointer", textTransform: "capitalize",
          }}>{s}</button>
        ))}
      </div>

      {isLoading && <p style={{ color: "#64748b" }}>Loading…</p>}

      <div style={{ display: "grid", gap: 12 }}>
        {requests.map(req => (
          <div key={req.id} style={{
            background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "20px 22px",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 16, color: "#1e293b", marginBottom: 4 }}>
                  {req.company_name}
                </div>
                <div style={{ fontSize: 13, color: "#64748b", display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 8 }}>
                  <span>🌐 {req.domain}</span>
                  <span>✉ {req.contact_email}</span>
                  <span>🕐 {formatDateTime(req.created_at)}</span>
                </div>
                {req.message && (
                  <p style={{ fontSize: 13, color: "#475569", background: "#f8fafc", borderRadius: 8,
                               padding: "8px 12px", margin: "8px 0 0", borderLeft: `3px solid ${TEAL}` }}>
                    "{req.message}"
                  </p>
                )}
                {req.status === "approved" && (
                  <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                    {req.setup_link && (
                      <button onClick={() => copyLink(req.setup_link, req.id)} style={{
                        background: "none", border: "1px solid #e2e8f0", borderRadius: 6,
                        padding: "4px 12px", fontSize: 12, cursor: "pointer",
                        color: copiedId === req.id && regenId !== req.id ? "#16a34a" : "#475569",
                      }}>
                        {copiedId === req.id && regenId !== req.id ? "✓ Copied" : "📋 Copy setup link"}
                      </button>
                    )}
                    <button onClick={() => regenerateLink(req.id)} disabled={regenLoading === req.id} style={{
                      background: regenId === req.id ? "#f0fdf4" : "none",
                      border: `1px solid ${regenId === req.id ? "#86efac" : "#e2e8f0"}`,
                      borderRadius: 6, padding: "4px 12px", fontSize: 12,
                      cursor: regenLoading === req.id ? "wait" : "pointer",
                      color: regenId === req.id ? "#16a34a" : "#1e3a8a", fontWeight: 600,
                    }}>
                      {regenLoading === req.id ? "Generating…" : regenId === req.id ? "✓ New link copied!" : "🔄 Regenerate link"}
                    </button>
                  </div>
                )}
              </div>

              {req.status === "pending" && (
                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  <AsyncButton onClick={() => approve(req.id)} style={{
                    background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`, color: "white",
                    border: "none", padding: "8px 18px", borderRadius: 8,
                    cursor: "pointer", fontWeight: 700, fontSize: 13,
                  }}>
                    {copiedId === req.id ? "✓ Link copied!" : "Approve"}
                  </AsyncButton>
                  <AsyncButton onClick={() => reject(req.id, req.company_name)} style={{
                    background: "none", border: "1px solid #fca5a5", color: "#dc2626",
                    padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontSize: 13,
                  }}>Reject</AsyncButton>
                </div>
              )}

              {req.status !== "pending" && (
                <span style={{
                  padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700,
                  background: req.status === "approved" ? "#dcfce7" : "#fee2e2",
                  color: req.status === "approved" ? "#166534" : "#991b1b",
                  textTransform: "capitalize", flexShrink: 0,
                }}>{req.status}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {!isLoading && requests.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
          <div style={{ fontSize: 40 }}>{filter === "pending" ? "📭" : "📋"}</div>
          <div style={{ marginTop: 10 }}>No {filter} requests.</div>
        </div>
      )}
    </>
  );
}

// ── Create tenant tab ─────────────────────────────────────────────────────────

const PLANS = [
  { value: "trial",      label: "Trial (14 days)",    desc: "75 employees, 400 CVs, 1500 AI ops" },
  { value: "starter",    label: "Starter — $129/mo",  desc: "50 employees cap, 75 CVs, 300 AI ops" },
  { value: "growth",     label: "Growth — $299/mo",   desc: "150 employees cap, 400 CVs, 1500 AI ops" },
  { value: "pro",        label: "Pro — $649/mo",      desc: "500 employees cap, 1500 CVs, 6000 AI ops" },
  { value: "enterprise", label: "Enterprise",         desc: "Unlimited" },
];

function CreateTab() {
  const qc = useQueryClient();
  const [companyName,   setCompanyName]   = useState("");
  const [domain,        setDomain]        = useState("");
  const [contactEmail,  setContactEmail]  = useState("");
  const [plan,          setPlan]          = useState("trial");
  const [trialDays,     setTrialDays]     = useState("14");
  const [loading,       setLoading]       = useState(false);
  const [result,        setResult]        = useState<{ setup_link: string; tenant_id: number } | null>(null);
  const [error,         setError]         = useState("");
  const [copied,        setCopied]        = useState(false);
  // null = "not specified", which keeps the historical grant-everything default for
  // self-serve. A hand-sold tenant needs an explicit list, because nothing will ever
  // reconcile it afterwards — there is no Stripe subscription to reconcile against.
  const [pickModules,   setPickModules]   = useState(false);
  const [modules,       setModules]       = useState<string[]>([]);

  async function handleCreate() {
    setLoading(true); setError(""); setResult(null);
    try {
      const r = await apiFetch("/api/admin/tenants/create", {
        method: "POST",
        body: JSON.stringify({
          company_name: companyName, domain, contact_email: contactEmail,
          plan, trial_days: parseInt(trialDays) || 14,
          ...(pickModules ? { modules } : {}),
        }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.detail || "Error creating tenant"); return; }
      setResult(d);
      qc.invalidateQueries({ queryKey: ["all-tenants"] });
      setCompanyName(""); setDomain(""); setContactEmail("");
      setPickModules(false); setModules([]);
    } finally {
      setLoading(false);
    }
  }

  async function copyLink() {
    if (!result) return;
    await navigator.clipboard.writeText(result.setup_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  }

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "11px 14px", borderRadius: 10, fontSize: 14,
    border: "1.5px solid #e2e8f0", outline: "none", boxSizing: "border-box",
    color: "#1e293b", marginBottom: 12,
  };

  return (
    <div style={{ maxWidth: 480 }}>
      <LoadingOverlay active={loading} />
      <p style={{ color: "#64748b", fontSize: 14, margin: "0 0 20px" }}>
        Create a new tenant workspace and generate a setup link for their admin.
      </p>

      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
                      padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#dc2626" }}>
          {error}
        </div>
      )}

      {result && (
        <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 10,
                      padding: 20, marginBottom: 20 }}>
          <div style={{ fontWeight: 700, color: "#166534", marginBottom: 8 }}>
            ✓ Tenant created (ID {result.tenant_id})
          </div>
          <p style={{ fontSize: 13, color: "#475569", margin: "0 0 12px" }}>
            Share this setup link with the admin. It expires in 24 hours.
            {" "}A setup email was also sent if SMTP is configured.
          </p>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <code style={{
              flex: 1, background: "white", border: "1px solid #e2e8f0", borderRadius: 6,
              padding: "8px 12px", fontSize: 12, wordBreak: "break-all",
            }}>{result.setup_link}</code>
            <button onClick={copyLink} style={{
              flexShrink: 0, background: copied ? "#16a34a" : NAVY, color: "white",
              border: "none", borderRadius: 7, padding: "8px 14px", fontSize: 13,
              fontWeight: 600, cursor: "pointer",
            }}>{copied ? "✓" : "Copy"}</button>
          </div>
        </div>
      )}

      <input style={inputStyle} type="text" placeholder="Company name" value={companyName}
        autoFocus onChange={e => setCompanyName(e.target.value)} />
      <input style={inputStyle} type="text" placeholder="Domain (e.g. acme.com)" value={domain}
        onChange={e => setDomain(e.target.value.replace(/^@/, ""))} />
      <input style={inputStyle} type="email" placeholder="Admin email" value={contactEmail}
        onChange={e => setContactEmail(e.target.value)} />

      <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>Plan</label>
      <select value={plan} onChange={e => setPlan(e.target.value)} style={{ ...inputStyle, marginBottom: 4 }}>
        {PLANS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
      </select>
      <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 12 }}>
        {PLANS.find(p => p.value === plan)?.desc}
      </div>

      <input style={inputStyle} type="number" placeholder="Trial days (default 14)" value={trialDays}
        min={1} max={365} onChange={e => setTrialDays(e.target.value)}
        onKeyDown={e => e.key === "Enter" && companyName && domain && contactEmail && handleCreate()} />

      {/* Modules. Left off, the tenant gets all six — right for a self-serve signup that
          Stripe reconciles moments later, wrong for a hand-sold deal where nothing ever
          reconciles and free access is permanent. */}
      <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, marginBottom: 12, overflow: "hidden" }}>
        <label style={{
          display: "flex", alignItems: "center", gap: 9, padding: "11px 13px",
          background: "#f8fafc", cursor: "pointer", minHeight: 44, boxSizing: "border-box",
        }}>
          <input
            type="checkbox"
            checked={pickModules}
            onChange={e => { setPickModules(e.target.checked); if (!e.target.checked) setModules([]); }}
            style={{ width: 16, height: 16, accentColor: NAVY }}
          />
          <span style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
            Choose modules{" "}
            <span style={{ fontWeight: 500, color: "#64748b" }}>
              (off = all six, the self-serve default)
            </span>
          </span>
        </label>

        {pickModules && (
          <div style={{ padding: "8px 13px 12px" }}>
            <p style={{ fontSize: 12, color: "#64748b", margin: "4px 0 9px" }}>
              This tenant has no Stripe subscription, so nothing will reconcile these later —
              what you pick here is what they keep. Select none for a Core-only workspace.
            </p>
            {MODULE_KEYS.map(key => {
              const checked = modules.includes(key);
              return (
                <label key={key} style={{
                  display: "flex", alignItems: "center", gap: 9, padding: "7px 9px",
                  minHeight: 44, borderRadius: 8, cursor: "pointer", marginBottom: 6,
                  boxSizing: "border-box",
                  border: `1.5px solid ${checked ? NAVY : "#e2e8f0"}`,
                  background: checked ? "#eff6ff" : "white",
                }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => setModules(prev =>
                      prev.includes(key) ? prev.filter(m => m !== key) : [...prev, key])}
                    style={{ width: 16, height: 16, accentColor: NAVY }}
                  />
                  <span style={{
                    display: "inline-block", width: 8, height: 8, borderRadius: 2,
                    background: MODULE_COLORS[key],
                  }} />
                  <span style={{ fontSize: 13, color: checked ? NAVY : "#334155", fontWeight: checked ? 700 : 500 }}>
                    {MODULE_LABELS[key]}
                  </span>
                </label>
              );
            })}
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
              {modules.length === 0
                ? "People only — no paid modules"
                : `People + ${modules.length} module${modules.length > 1 ? "s" : ""}`}
            </div>
          </div>
        )}
      </div>

      <button
        onClick={handleCreate}
        disabled={!companyName || !domain || !contactEmail || loading}
        style={{
          width: "100%", padding: "12px 0", borderRadius: 10, border: "none",
          background: !companyName || !domain || !contactEmail
            ? "#94a3b8" : `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
          color: "white", fontSize: 15, fontWeight: 700,
          cursor: !companyName || !domain || !contactEmail || loading ? "not-allowed" : "pointer",
        }}
      >
        {loading ? "Creating…" : "Create Tenant & Get Setup Link →"}
      </button>
    </div>
  );
}

// ── AI Config tab ─────────────────────────────────────────────────────────────

interface TenantAiConfig {
  tenant_id: number; tenant_name: string; domain: string;
  ai_provider: string; ollama_model: string;
  used_this_month: number; cost_this_month: number;
  by_feature: { feature: string; calls: number; cost: number }[];
  trend: { yr: number; mo: number; calls: number; cost: number }[];
}

const OLLAMA_MODELS = ["qwen2.5:7b", "qwen2.5:3b", "llama3.2:3b", "mistral:7b"];
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const FEATURE_LABELS: Record<string, string> = {
  chat: "AI Chatbot", cv_scoring: "CV Scoring", job_description: "Job Description",
  offer_letter: "Offer Letter", hr_letter: "HR Letter",
  outreach_email: "Outreach Email", interview_questions: "Interview Questions",
  candidate_compare: "Candidate Compare", talent_pool: "Talent Pool",
};

function AiConfigTab() {
  const qc = useQueryClient();
  const { data: raw, isLoading } = useQuery<any>({
    queryKey: ["tenant-ai-configs"], queryFn: getTenantAiConfigs,
  });

  const configs: TenantAiConfig[] = raw?.tenants ?? [];
  const platformCalls: number = raw?.platform_calls ?? 0;
  const platformCost: number  = raw?.platform_cost  ?? 0;

  const [edits,    setEdits]    = useState<Record<number, Partial<TenantAiConfig>>>({});
  const [saving,   setSaving]   = useState<number | null>(null);
  const [saved,    setSaved]    = useState<number | null>(null);
  const [saveErr,  setSaveErr]  = useState<Record<number, string>>({});
  const [expanded, setExpanded] = useState<number | null>(null);

  function getField<K extends keyof TenantAiConfig>(t: TenantAiConfig, k: K): TenantAiConfig[K] {
    return (edits[t.tenant_id]?.[k] ?? t[k]) as TenantAiConfig[K];
  }
  function setField(tenantId: number, field: string, value: any) {
    setEdits(e => ({ ...e, [tenantId]: { ...e[tenantId], [field]: value } }));
  }

  async function save(t: TenantAiConfig) {
    setSaving(t.tenant_id);
    setSaveErr(e => { const n = { ...e }; delete n[t.tenant_id]; return n; });
    try {
      await updateTenantAiConfig(t.tenant_id, {
        ai_provider:  getField(t, "ai_provider"),
        ollama_model: getField(t, "ollama_model"),
      });
      await qc.refetchQueries({ queryKey: ["tenant-ai-configs"] });
      setEdits(e => { const n = { ...e }; delete n[t.tenant_id]; return n; });
      setSaved(t.tenant_id); setTimeout(() => setSaved(null), 2500);
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? err?.message ?? "Save failed";
      setSaveErr(e => ({ ...e, [t.tenant_id]: msg }));
      setTimeout(() => setSaveErr(e => { const n = { ...e }; delete n[t.tenant_id]; return n; }), 4000);
    } finally { setSaving(null); }
  }

  // Aggregate feature breakdown across all tenants
  const platformByFeature = configs.reduce<Record<string, { calls: number; cost: number }>>((acc, t) => {
    t.by_feature.forEach(f => {
      if (!acc[f.feature]) acc[f.feature] = { calls: 0, cost: 0 };
      acc[f.feature].calls += f.calls;
      acc[f.feature].cost  += f.cost;
    });
    return acc;
  }, {});
  const platformFeatures = Object.entries(platformByFeature)
    .map(([feature, v]) => ({ feature, ...v }))
    .sort((a, b) => b.calls - a.calls);

  // Aggregate monthly trend across all tenants
  const trendMap = configs.reduce<Record<string, { calls: number; cost: number }>>((acc, t) => {
    t.trend.forEach(r => {
      const key = `${r.yr}-${String(r.mo).padStart(2,"0")}`;
      if (!acc[key]) acc[key] = { calls: 0, cost: 0 };
      acc[key].calls += r.calls;
      acc[key].cost  += r.cost;
    });
    return acc;
  }, {});
  const platformTrend = Object.entries(trendMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-6)
    .map(([key, v]) => ({ key, mo: parseInt(key.split("-")[1]), ...v }));

  const selStyle: React.CSSProperties = {
    padding: "6px 10px", borderRadius: 6, border: "1.5px solid #e2e8f0",
    fontSize: 13, color: "#1e293b", background: "white", cursor: "pointer",
  };

  if (isLoading) return <p style={{ color: "#64748b" }}>Loading…</p>;

  return (
    <>
      <LoadingOverlay active={saving !== null} />
      {/* ── Section 1: Platform Cost Overview ── */}
      <div style={{ marginBottom: 28 }}>

        {/* Summary cards */}
        <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          {[
            { label: "Calls this month",     value: platformCalls.toLocaleString(), color: NAVY,      icon: "⚡" },
            { label: "Cost this month",       value: `$${platformCost.toFixed(4)}`,  color: "#dc2626", icon: "💰" },
            { label: "Active tenants",        value: configs.length,                 color: "#0f766e", icon: "🏢" },
            { label: "Tenants with AI usage", value: configs.filter(t => t.used_this_month > 0).length, color: "#7c3aed", icon: "📊" },
          ].map(s => (
            <div key={s.label} style={{
              flex: "1 1 160px", background: "white", border: "1px solid #e2e8f0",
              borderRadius: 10, padding: "14px 18px", borderTop: `3px solid ${s.color}`,
            }}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>{s.icon}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Platform feature breakdown + trend side by side */}
        {platformCalls > 0 && (
          <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "20px 24px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}>

            {/* Feature breakdown */}
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 14 }}>Platform usage by feature</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {platformFeatures.map(f => {
                  const pct = platformCalls > 0 ? Math.round(f.calls / platformCalls * 100) : 0;
                  return (
                    <div key={f.feature}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 12 }}>
                        <span style={{ fontWeight: 600, color: "#334155" }}>{FEATURE_LABELS[f.feature] || f.feature}</span>
                        <span style={{ color: "#64748b" }}>
                          {f.calls} calls
                          {f.cost > 0 && <span style={{ color: "#dc2626", marginLeft: 8 }}>${f.cost.toFixed(4)}</span>}
                        </span>
                      </div>
                      <div style={{ height: 6, background: "#f1f5f9", borderRadius: 3 }}>
                        <div style={{ height: "100%", width: `${pct}%`, borderRadius: 3, background: `linear-gradient(90deg, ${NAVY}, ${TEAL})` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Monthly trend */}
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 14 }}>Platform trend (last 6 months)</div>
              {platformTrend.length === 0
                ? <div style={{ fontSize: 12, color: "#94a3b8" }}>No history yet</div>
                : (
                  <>
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 64 }}>
                      {(() => {
                        const max = Math.max(...platformTrend.map(r => r.calls), 1);
                        return platformTrend.map(r => (
                          <div key={r.key} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <div style={{ fontSize: 10, color: "#64748b", marginBottom: 3 }}>{r.calls}</div>
                            <div style={{
                              width: "100%", borderRadius: "3px 3px 0 0",
                              height: `${Math.max(4, Math.round(r.calls / max * 44))}px`,
                              background: `linear-gradient(180deg, ${TEAL}, ${NAVY})`,
                            }} />
                          </div>
                        ));
                      })()}
                    </div>
                    <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                      {platformTrend.map(r => (
                        <div key={r.key} style={{ flex: 1, fontSize: 10, color: "#94a3b8", textAlign: "center" }}>
                          {MONTH_NAMES[r.mo - 1]}
                        </div>
                      ))}
                    </div>
                    {platformCost > 0 && (
                      <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
                        Total Gemini cost this month: <strong style={{ color: "#dc2626" }}>${platformCost.toFixed(4)}</strong>
                      </div>
                    )}
                  </>
                )
              }
            </div>
          </div>
        )}
      </div>

      {/* ── Section 2: Tenant Provider Settings ── */}
      <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9", background: "#f8fafc" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>Tenant AI Provider Settings</div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>Change AI provider or model per tenant. Quota is defined by each tenant's subscription plan.</div>
        </div>

        {/* Table header */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 180px 100px 100px 140px 40px", gap: 0, padding: "8px 20px", borderBottom: "1px solid #f1f5f9", background: "#f8fafc" }}>
          {["Tenant", "Provider", "Calls", "Cost", "", ""].map((h, i) => (
            <div key={i} style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</div>
          ))}
        </div>

        {/* Rows */}
        {configs.map(t => {
          const isDirty  = !!edits[t.tenant_id];
          const provider = getField(t, "ai_provider");
          const isOpen   = expanded === t.tenant_id;
          const err      = saveErr[t.tenant_id];

          return (
            <div key={t.tenant_id} style={{ borderBottom: "1px solid #f1f5f9" }}>
              <div style={{
                display: "grid", gridTemplateColumns: "1fr 180px 100px 100px 140px 40px",
                gap: 0, padding: "12px 20px", alignItems: "center",
                background: isDirty ? "#f0f7ff" : "white",
              }}>
                {/* Tenant */}
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>{t.tenant_name}</div>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>{t.domain}</div>
                </div>

                {/* Provider */}
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <select value={provider} style={selStyle}
                    onChange={e => setField(t.tenant_id, "ai_provider", e.target.value)}>
                    <option value="ollama">Ollama (local)</option>
                    <option value="gemini">Gemini (Google)</option>
                  </select>
                  {provider === "ollama" && (
                    <select value={getField(t, "ollama_model")} style={{ ...selStyle, fontSize: 11 }}
                      onChange={e => setField(t.tenant_id, "ollama_model", e.target.value)}>
                      {OLLAMA_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  )}
                </div>

                {/* Calls */}
                <div style={{ fontSize: 13, fontWeight: 600, color: t.used_this_month > 0 ? NAVY : "#94a3b8" }}>
                  {t.used_this_month.toLocaleString()}
                </div>

                {/* Cost */}
                <div style={{ fontSize: 13, fontWeight: 600, color: t.cost_this_month > 0 ? "#dc2626" : "#94a3b8" }}>
                  ${t.cost_this_month.toFixed(4)}
                </div>

                {/* Save */}
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {err && <div style={{ fontSize: 10, color: "#dc2626" }}>⚠ {err}</div>}
                  <button onClick={() => save(t)} disabled={saving === t.tenant_id || !isDirty} style={{
                    padding: "6px 14px", borderRadius: 6, border: "none", fontSize: 12, fontWeight: 700,
                    cursor: !isDirty ? "default" : saving === t.tenant_id ? "not-allowed" : "pointer",
                    background: saved === t.tenant_id ? "#10b981"
                      : !isDirty ? "#f1f5f9"
                      : `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
                    color: !isDirty ? "#94a3b8" : "white",
                  }}>
                    {saving === t.tenant_id ? "…" : saved === t.tenant_id ? "✓ Saved" : "Save"}
                  </button>
                </div>

                {/* Expand */}
                <button onClick={() => setExpanded(isOpen ? null : t.tenant_id)} style={{
                  padding: "6px 8px", borderRadius: 6, border: "1px solid #e2e8f0",
                  background: "white", cursor: "pointer", fontSize: 12, color: "#64748b",
                }}>{isOpen ? "▲" : "▼"}</button>
              </div>

              {/* Expanded: per-tenant feature breakdown */}
              {isOpen && (
                <div style={{ padding: "14px 20px 18px", background: "#f8fafc", borderTop: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 10 }}>Breakdown by feature</div>
                  {t.by_feature.length === 0
                    ? <div style={{ fontSize: 12, color: "#94a3b8" }}>No usage yet this month</div>
                    : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {t.by_feature.sort((a, b) => b.calls - a.calls).map(f => {
                          const pct = t.used_this_month > 0 ? Math.round(f.calls / t.used_this_month * 100) : 0;
                          return (
                            <div key={f.feature} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <div style={{ width: 130, fontSize: 12, color: "#334155", flexShrink: 0 }}>
                                {FEATURE_LABELS[f.feature] || f.feature}
                              </div>
                              <div style={{ flex: 1, height: 5, background: "#e2e8f0", borderRadius: 3 }}>
                                <div style={{ height: "100%", width: `${pct}%`, borderRadius: 3, background: `linear-gradient(90deg, ${NAVY}, ${TEAL})` }} />
                              </div>
                              <div style={{ fontSize: 12, color: "#64748b", width: 55, textAlign: "right", flexShrink: 0 }}>{f.calls} calls</div>
                              {f.cost > 0 && <div style={{ fontSize: 11, color: "#dc2626", width: 52, textAlign: "right", flexShrink: 0 }}>${f.cost.toFixed(4)}</div>}
                            </div>
                          );
                        })}
                      </div>
                    )
                  }
                </div>
              )}
            </div>
          );
        })}

        {configs.length === 0 && (
          <div style={{ textAlign: "center", padding: "48px 0", color: "#94a3b8" }}>
            <div style={{ fontSize: 36 }}>🤖</div>
            <div style={{ marginTop: 8, fontSize: 13 }}>No tenants found.</div>
          </div>
        )}
      </div>
    </>
  );
}

// ── Billing tab ───────────────────────────────────────────────────────────────

const PLAN_COLORS: Record<string, string> = {
  trial: "#f59e0b", starter: "#3b82f6", growth: "#8b5cf6", pro: "#10b981", enterprise: "#1e3a8a",
};
const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  active:      { bg: "#dcfce7", text: "#166534", border: "#86efac" },
  trialing:    { bg: "#dbeafe", text: "#1e40af", border: "#93c5fd" },
  grace:       { bg: "#fef9c3", text: "#854d0e", border: "#fde047" },
  readonly:    { bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
  expired:     { bg: "#f1f5f9", text: "#475569", border: "#cbd5e1" },
  cancelled:   { bg: "#f1f5f9", text: "#475569", border: "#cbd5e1" },
};

interface BillingRow {
  id: number; name: string; domain: string; is_active: boolean;
  plan: string; plan_status: string; effective_status: string;
  is_grandfathered: boolean; trial_ends_at: string | null;
  trial_days_left: number | null; employee_limit: number;
  subscription_ends_at: string | null;
  stripe_customer_id: string | null;
  monthly_revenue_usd: number;
  manual_monthly_revenue_usd: number | null;
  meters: Record<string, { used: number; limit: number }>;
  modules?: ModuleMap;
}

function MeterBar({ used, limit, color }: { used: number; limit: number; color: string }) {
  const pct = limit && limit < 9000 ? Math.min(Math.round(used / limit * 100), 100) : 0;
  const barColor = pct >= 100 ? "#dc2626" : pct >= 80 ? "#f59e0b" : color;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
      <div style={{ flex: 1, background: "#f1f5f9", borderRadius: 4, height: 6, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, background: barColor, height: "100%", borderRadius: 4, transition: "width 0.3s" }} />
      </div>
      <span style={{ color: "#64748b", minWidth: 60, textAlign: "right" }}>
        {limit >= 9000 ? "∞" : `${used}/${limit}`}
      </span>
    </div>
  );
}

// Default employee limits per plan — must match backend PLAN_LIMITS
const PLAN_EMP_LIMITS: Record<string, number> = {
  trial: 75, starter: 50, growth: 150, pro: 500, enterprise: 9999,
};

function PlanEditor({ tenant, onClose }: { tenant: BillingRow; onClose: () => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [plan, setPlan] = useState(tenant.plan);
  const [empLimit, setEmpLimit] = useState(String(tenant.employee_limit));
  const [trialDays, setTrialDays] = useState("");
  const [subEndsAt, setSubEndsAt] = useState(tenant.subscription_ends_at?.slice(0, 10) ?? "");
  const [manualRevenue, setManualRevenue] = useState(tenant.manual_monthly_revenue_usd != null ? String(tenant.manual_monthly_revenue_usd) : "");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [loggingPayment, setLoggingPayment] = useState(false);
  const [saving, setSaving] = useState(false);

  // Modules were editable only from the Tenants tab, which is not where anyone goes to
  // change what a customer is paying for. For a manually-billed tenant this is the only
  // screen that matters: nothing reconciles them against Stripe, so what is set here is
  // what they keep.
  const [modDraft, setModDraft] = useState<Record<string, boolean>>(
    () => Object.fromEntries(MODULE_KEYS.map(k => [k, tenant.modules?.[k] === true])),
  );
  const modOriginal = (k: ModuleKey) => tenant.modules?.[k] === true;
  const modGrant  = MODULE_KEYS.filter(k => modDraft[k] && !modOriginal(k));
  const modRevoke = MODULE_KEYS.filter(k => !modDraft[k] && modOriginal(k));

  async function logPayment() {
    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) {
      toast("Enter a valid payment amount", "error");
      return;
    }
    setLoggingPayment(true);
    try {
      await logManualPayment(tenant.id, { amount_usd: amount, method: paymentMethod || undefined });
      toast("Payment logged", "success");
      setPaymentAmount("");
      setPaymentMethod("");
    } catch {
      toast("Failed to log payment", "error");
    } finally {
      setLoggingPayment(false);
    }
  }

  function handlePlanChange(newPlan: string) {
    setPlan(newPlan);
    // Auto-update employee limit to plan default when plan changes
    setEmpLimit(String(PLAN_EMP_LIMITS[newPlan] ?? tenant.employee_limit));
  }

  async function save() {
    setSaving(true);
    try {
      const payload: Record<string, any> = { plan, employee_limit: parseInt(empLimit) };
      if (trialDays) payload.extend_trial_days = parseInt(trialDays);
      payload.subscription_ends_at = subEndsAt || null;
      payload.manual_monthly_revenue_usd = manualRevenue ? parseFloat(manualRevenue) : null;
      // Entitlements first: a plan change that syncs to Stripe can take time, and a
      // failure there should not silently discard the module edit made in the same form.
      if (modGrant.length || modRevoke.length) {
        await updateTenantModules(tenant.id, { grant: modGrant, revoke: modRevoke });
      }
      const result = await updateTenantPlan(tenant.id, payload);
      if (result?.stripe_synced) {
        toast(result.usage_warning ? `Plan synced to Stripe. ${result.usage_warning}` : "Plan updated and synced to Stripe", "success");
      } else if (result?.usage_warning) {
        // DB-only change (e.g. enterprise carve-out) on a tenant with a still-live Stripe
        // subscription — flag it as a warning, not a plain success, so the admin doesn't
        // walk away thinking Stripe billing was handled too.
        toast(result.usage_warning, "error");
      } else {
        toast("Plan updated", "success");
      }
      qc.invalidateQueries({ queryKey: ["sa-billing"] });
      qc.invalidateQueries({ queryKey: ["all-tenants"] });
      onClose();
    } catch (e: any) {
      // AC7 can now legitimately block the edit (e.g. no Stripe price configured for the
      // target plan/period) — surface that reason instead of a generic failure message.
      toast(e?.response?.data?.detail || "Failed to update plan", "error");
    } finally {
      setSaving(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: 8,
    fontSize: 13, color: "#1e293b", boxSizing: "border-box", outline: "none",
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center",
    }} onClick={onClose}>
      <LoadingOverlay active={saving} />
      <div style={{
        background: "white", borderRadius: 14, padding: 28, width: 380, maxWidth: "90vw",
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
      }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 4px", color: "#1e293b", fontSize: 17 }}>Manage Plan</h3>
        <p style={{ margin: "0 0 20px", fontSize: 13, color: "#64748b" }}>{tenant.name} — {tenant.domain}</p>

        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>Plan</label>
        <select value={plan} onChange={e => handlePlanChange(e.target.value)} style={{ ...inputStyle, marginBottom: 14 }}>
          {["trial", "starter", "growth", "pro", "enterprise"].map(p => (
            <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
          ))}
        </select>

        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>
          Employee Limit
          <span style={{ fontWeight: 400, color: "#94a3b8", marginLeft: 6 }}>
            (plan default: {PLAN_EMP_LIMITS[plan] ?? "—"})
          </span>
        </label>
        <input type="number" value={empLimit} onChange={e => setEmpLimit(e.target.value)} style={{ ...inputStyle, marginBottom: 14 }} min={1} />

        {plan === "trial" ? (
          <>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>
              Extend Trial (days from today) — leave blank to keep current dates
            </label>
            <input type="number" value={trialDays} onChange={e => setTrialDays(e.target.value)}
              style={{ ...inputStyle, marginBottom: 20 }} placeholder="e.g. 14" min={1} />
          </>
        ) : (
          <>
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#166534" }}>
              Paid plan — active until subscription end date. Auto-deactivates on expiry (blocks login).
            </div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>
              Subscription End Date
              <span style={{ fontWeight: 400, color: "#94a3b8", marginLeft: 6 }}>leave blank for no expiry</span>
            </label>
            <DateInput value={subEndsAt} onChange={e => setSubEndsAt(e.target.value)}
              style={{ ...inputStyle, marginBottom: 20 }} />
          </>
        )}

        {/* Manual billing (billing-module-enhancement AC18) — only relevant for tenants
            with no live Stripe subscription, invoiced outside Stripe. */}
        {!tenant.stripe_customer_id && (
          <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "12px 14px", marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", marginBottom: 10 }}>
              Manually billed — no Stripe subscription
            </div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>
              Manual monthly revenue (USD)
              <span style={{ fontWeight: 400, color: "#94a3b8", marginLeft: 6 }}>feeds MRR/ARR reporting</span>
            </label>
            <input type="number" value={manualRevenue} onChange={e => setManualRevenue(e.target.value)}
              style={{ ...inputStyle, marginBottom: 12 }} placeholder="e.g. 500" min={0} step="0.01" />

            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>
              Log a payment received
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <input type="number" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)}
                style={{ ...inputStyle, flex: 1 }} placeholder="Amount $" min={0} step="0.01" />
              <input type="text" value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
                style={{ ...inputStyle, flex: 1 }} placeholder="Method (optional)" />
              <button onClick={logPayment} disabled={loggingPayment} style={{
                padding: "0 14px", borderRadius: 8, border: "none", background: "#92400e",
                color: "white", fontSize: 13, fontWeight: 700, cursor: "pointer",
              }}>Log</button>
            </div>
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: "#475569", display: "block", marginBottom: 6 }}>
            Modules
          </label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            {MODULE_KEYS.map(key => {
              const on = modDraft[key];
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setModDraft(d => ({ ...d, [key]: !d[key] }))}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    fontSize: 12, fontWeight: 600, borderRadius: 999,
                    padding: "7px 12px", cursor: "pointer", minHeight: 34,
                    background: on ? "#eff6ff" : "white",
                    border: `1.5px solid ${on ? NAVY : "#e2e8f0"}`,
                    color: on ? NAVY : "#94a3b8",
                  }}
                >
                  <span style={{
                    width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
                    background: on ? MODULE_COLORS[key] : "#cbd5e1",
                  }} />
                  {MODULE_LABELS[key]}
                </button>
              );
            })}
          </div>
          {(modGrant.length > 0 || modRevoke.length > 0) && (
            <div style={{ fontSize: 12, color: "#b45309", marginTop: 7, fontWeight: 600 }}>
              On save:{modGrant.length ? ` grant ${modGrant.length}` : ""}
              {modGrant.length && modRevoke.length ? " ·" : ""}
              {modRevoke.length ? ` revoke ${modRevoke.length}` : ""}
              {modRevoke.length ? " — data is kept, access is turned off" : ""}
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: "10px 0", borderRadius: 8, border: "1.5px solid #e2e8f0",
            background: "white", fontSize: 14, cursor: "pointer", color: "#475569",
          }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{
            flex: 2, padding: "10px 0", borderRadius: 8, border: "none",
            background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
            color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer",
          }}>{saving ? "Saving…" : "Save Plan"}</button>
        </div>
      </div>
    </div>
  );
}

function BillingTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data: rows = [], isLoading } = useQuery<BillingRow[]>({
    queryKey: ["sa-billing"], queryFn: getTenantBilling, refetchInterval: 60_000,
  });
  const { data: trends } = useQuery({
    queryKey: ["sa-billing-trends"], queryFn: () => getBillingTrends(90),
    staleTime: 5 * 60 * 1000,
  });
  const { data: anomalies = [], refetch: refetchAnomalies } = useQuery({
    queryKey: ["sa-billing-anomalies"], queryFn: () => getBillingAnomalies("open"),
    staleTime: 60 * 1000,
  });
  const [editing, setEditing] = useState<BillingRow | null>(null);
  const [toggling, setToggling] = useState<number | null>(null);
  const [resolving, setResolving] = useState<number | null>(null);

  async function resolveAnomaly(id: number) {
    setResolving(id);
    try {
      await resolveBillingAnomaly(id);
      refetchAnomalies();
    } catch {
      toast("Failed to resolve", "error");
    } finally {
      setResolving(null);
    }
  }

  async function toggleActive(r: BillingRow) {
    setToggling(r.id);
    try {
      await updateTenantPlan(r.id, { is_active: !r.is_active });
      toast(`${r.name} ${!r.is_active ? "activated" : "deactivated"}`, "success");
      qc.invalidateQueries({ queryKey: ["sa-billing"] });
    } catch {
      toast("Failed to update status", "error");
    } finally { setToggling(null); }
  }

  if (isLoading) return <p style={{ color: "#64748b" }}>Loading…</p>;

  const METERS = [
    { key: "employees", label: "Employees" },
    { key: "cv_processed", label: "CVs" },
    { key: "ai_operation", label: "AI Ops" },
    { key: "ai_doc", label: "AI Docs" },
    { key: "expense_captures_processed", label: "Expense Captures" },
    { key: "screening_interviews", label: "Screenings" },
  ];

  const mrr = rows.reduce((s, r) => s + (r.monthly_revenue_usd || 0), 0);
  const paidCount = rows.filter(r => r.monthly_revenue_usd > 0).length;
  // Covers both the no-card trial (plan==="trial") and the self-serve card-authorized
  // trial (effective_status==="trialing", real plan tier) — trial_days_left is set by
  // the backend for both, so it alone is the reliable signal.
  const trialCount = rows.filter(r => r.trial_days_left != null && (r.effective_status === "active" || r.effective_status === "trialing")).length;
  const graceCount = rows.filter(r => r.effective_status === "grace" || r.effective_status === "readonly").length;

  const statCards = [
    { label: "MRR", value: `$${mrr.toLocaleString()}`, color: "#10b981", icon: "💰" },
    { label: "ARR (est.)", value: `$${(mrr * 12).toLocaleString()}`, color: "#3b82f6", icon: "📈" },
    { label: "Paying tenants", value: paidCount, color: "#8b5cf6", icon: "🏢" },
    { label: "On trial", value: trialCount, color: "#f59e0b", icon: "⏳" },
    { label: "Grace / at-risk", value: graceCount, color: graceCount > 0 ? "#dc2626" : "#94a3b8", icon: "⚠️" },
  ];

  return (
    <>
      <LoadingOverlay active={toggling !== null} />
      {editing && <PlanEditor tenant={editing} onClose={() => setEditing(null)} />}

      {/* MRR summary */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
        {statCards.map(c => (
          <div key={c.label} style={{
            background: "white", border: "1px solid #e2e8f0", borderRadius: 12,
            padding: "14px 20px", minWidth: 120, flex: "1 1 120px",
          }}>
            <div style={{ fontSize: 20 }}>{c.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: c.color, marginTop: 4 }}>{c.value}</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* ── MRR/ARR trend (billing-module-enhancement AC12) ── */}
      {trends?.points?.length > 1 && (
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 20px", marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>
              MRR trend — last {trends.range_days} days
              <span style={{ fontWeight: 400, color: "#94a3b8", marginLeft: 8, fontSize: 12 }}>
                (real Stripe amounts, annual normalized to monthly)
              </span>
            </div>
            {trends.churned_in_range > 0 && (
              <span style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }}>
                {trends.churned_in_range} cancelled in range
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 3, alignItems: "flex-end", height: 90 }}>
            {trends.points.map((p: any) => {
              const maxMrr = Math.max(...trends.points.map((x: any) => x.mrr_usd), 1);
              const h = Math.max(3, Math.round((p.mrr_usd / maxMrr) * 80));
              return (
                <div key={p.date} title={`${p.date}: $${p.mrr_usd}`} style={{
                  flex: 1, height: h, background: "#10b981", borderRadius: "2px 2px 0 0",
                  opacity: 0.85, minWidth: 2,
                }} />
              );
            })}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
            <span>{trends.points[0].date}</span>
            <span>${trends.points[trends.points.length - 1].mrr_usd.toLocaleString()}/mo now</span>
            <span>{trends.points[trends.points.length - 1].date}</span>
          </div>
        </div>
      )}

      {/* ── Needs Attention queue (billing-module-enhancement AC8/AC17) ── */}
      {anomalies.length > 0 && (
        <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 12, padding: "16px 20px", marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e", marginBottom: 10 }}>
            ⚠️ Needs Attention ({anomalies.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {anomalies.map((a: any) => (
              <div key={a.id} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                background: "white", borderRadius: 8, padding: "10px 14px", fontSize: 13,
              }}>
                <div>
                  <span style={{ fontWeight: 600, color: "#1e293b" }}>{a.tenant_name || `Tenant #${a.tenant_id}`}</span>
                  <span style={{ color: "#94a3b8", marginLeft: 8, fontSize: 11 }}>{a.anomaly_type}</span>
                  <div style={{ color: "#64748b", fontSize: 12, marginTop: 2 }}>{a.detail}</div>
                </div>
                <button onClick={() => resolveAnomaly(a.id)} disabled={resolving === a.id} style={{
                  padding: "6px 14px", borderRadius: 6, border: "1.5px solid #e2e8f0",
                  background: "white", fontSize: 12, fontWeight: 600, color: "#475569", cursor: "pointer",
                  whiteSpace: "nowrap",
                }}>{resolving === a.id ? "…" : "Resolve"}</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <p style={{ color: "#64748b", fontSize: 14, margin: "0 0 20px" }}>
        {rows.length} workspace{rows.length !== 1 ? "s" : ""} — current month usage
      </p>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f8fafc" }}>
              <th style={thStyle}>Workspace</th>
              <th style={thStyle}>Plan</th>
              <th style={thStyle}>Active</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>MRR</th>
              <th style={thStyle}>Trial ends</th>
              <th style={thStyle}>Sub ends</th>
              {METERS.map(m => <th key={m.key} style={thStyle}>{m.label}</th>)}
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const sc = STATUS_COLORS[r.effective_status] || STATUS_COLORS.active;
              return (
                <tr key={r.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600, color: "#1e293b" }}>{r.name}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ color: "#94a3b8", fontSize: 11 }}>{r.domain}</span>
                      {r.stripe_customer_id && (
                        <a
                          href={`https://dashboard.stripe.com/customers/${r.stripe_customer_id}`}
                          target="_blank" rel="noreferrer"
                          style={{ fontSize: 10, color: "#6366f1", textDecoration: "none", fontWeight: 600 }}
                          title="Open in Stripe"
                        >Stripe →</a>
                      )}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      background: PLAN_COLORS[r.plan] + "22", color: PLAN_COLORS[r.plan] || NAVY,
                      border: `1px solid ${PLAN_COLORS[r.plan] || NAVY}44`,
                      padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                    }}>{r.plan}</span>
                    {r.is_grandfathered && (
                      <span style={{ marginLeft: 4, fontSize: 10, color: "#94a3b8" }}>legacy</span>
                    )}
                  </td>
                  {/* Active toggle */}
                  <td style={tdStyle}>
                    <button
                      onClick={() => toggleActive(r)}
                      disabled={toggling === r.id}
                      title={r.is_active ? "Click to deactivate (blocks login)" : "Click to activate"}
                      style={{
                        padding: "3px 10px", borderRadius: 20, border: "none", fontSize: 11, fontWeight: 700,
                        cursor: toggling === r.id ? "not-allowed" : "pointer",
                        background: r.is_active ? "#dcfce7" : "#fee2e2",
                        color: r.is_active ? "#166534" : "#991b1b",
                        opacity: toggling === r.id ? 0.6 : 1,
                      }}>
                      {toggling === r.id ? "…" : r.is_active ? "Active" : "Inactive"}
                    </button>
                  </td>

                  <td style={tdStyle}>
                    <span style={{
                      background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`,
                      padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                    }}>{r.effective_status}</span>
                  </td>
                  <td style={tdStyle}>
                    {r.monthly_revenue_usd > 0 ? (
                      <span style={{ fontWeight: 700, color: "#10b981" }}>${r.monthly_revenue_usd}/mo</span>
                    ) : (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {r.trial_days_left == null || r.is_grandfathered
                      ? <span style={{ color: "#94a3b8" }}>—</span>
                      : r.trial_ends_at ? (
                          <span style={{ color: (r.trial_days_left ?? 0) <= 3 ? "#dc2626" : "#475569" }}>
                            {r.trial_ends_at.slice(0, 10)}
                            {r.trial_days_left != null && r.trial_days_left >= 0 && (
                              <span style={{ color: "#94a3b8", fontSize: 11 }}> ({r.trial_days_left}d)</span>
                            )}
                          </span>
                        ) : <span style={{ color: "#94a3b8" }}>—</span>}
                  </td>
                  {/* Subscription end date */}
                  <td style={tdStyle}>
                    {r.subscription_ends_at ? (
                      <span style={{ fontSize: 12, color: r.subscription_ends_at.slice(0, 10) < new Date().toISOString().slice(0, 10) ? "#dc2626" : "#475569" }}>
                        {r.subscription_ends_at.slice(0, 10)}
                      </span>
                    ) : <span style={{ color: "#94a3b8" }}>—</span>}
                  </td>

                  {METERS.map(m => {
                    const mv = r.meters[m.key];
                    if (!mv) return <td key={m.key} style={tdStyle}>—</td>;
                    const pct = mv.limit && mv.limit < 9000 ? Math.round(mv.used / mv.limit * 100) : 0;
                    const over = mv.limit < 9000 && mv.used > mv.limit;
                    return (
                      <td key={m.key} style={tdStyle}>
                        <span style={{ fontSize: 12, fontWeight: over ? 700 : 400, color: over ? "#dc2626" : pct >= 80 ? "#f59e0b" : "#475569" }}>
                          {mv.limit >= 9000 ? `${mv.used} / ∞` : `${mv.used} / ${mv.limit}`}
                        </span>
                      </td>
                    );
                  })}
                  <td style={tdStyle}>
                    <button onClick={() => setEditing(r)} style={{
                      background: "none", border: `1px solid ${NAVY}`, color: NAVY,
                      padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600,
                    }}>Edit Plan</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Audit Log tab ─────────────────────────────────────────────────────────────

const ACTION_COLORS: Record<string, string> = {
  "tenant.toggle": "#f59e0b", "tenant.update": "#3b82f6",
  "user.role_change": "#8b5cf6", "user.remove": "#ef4444",
  "user.tenant_reassign": "#ec4899",
  "invite.create": "#10b981", "invite.revoke": "#f97316", "invite.resend": "#06b6d4",
};

function actionColor(action: string): string {
  if (ACTION_COLORS[action]) return ACTION_COLORS[action];
  if (action.startsWith("tenant")) return "#3b82f6";
  if (action.startsWith("user"))   return "#8b5cf6";
  if (action.startsWith("invite")) return "#10b981";
  if (action.startsWith("job"))    return "#0ea5e9";
  if (action.startsWith("leave"))  return "#f59e0b";
  return "#64748b";
}

function AuditTab({ tenants }: { tenants: { id: number; name: string }[] }) {
  const PAGE = 50;
  const [page,          setPage]          = useState(0);
  const [filterAction,  setFilterAction]  = useState("");
  const [filterTenant,  setFilterTenant]  = useState<number | "">("");
  const [expanded,      setExpanded]      = useState<number | null>(null);

  const params = {
    limit:     PAGE,
    offset:    page * PAGE,
    ...(filterAction  ? { action:    filterAction }           : {}),
    ...(filterTenant  ? { tenant_id: filterTenant as number } : {}),
  };

  const { data, isLoading } = useQuery({
    queryKey: ["platform-audit", params],
    queryFn:  () => getPlatformAuditLog(params),
    refetchInterval: 30_000,
  });

  const logs: any[]  = data?.logs  ?? [];
  const total: number = data?.total ?? 0;
  const pages = Math.ceil(total / PAGE);

  function resetFilters() {
    setFilterAction(""); setFilterTenant(""); setPage(0);
  }

  return (
    <>
      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        <input
          type="text"
          placeholder="Filter by action…"
          value={filterAction}
          onChange={e => { setFilterAction(e.target.value); setPage(0); }}
          style={{
            padding: "8px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0",
            fontSize: 13, color: "#1e293b", outline: "none", width: 200,
          }}
        />
        <select
          value={filterTenant}
          onChange={e => { setFilterTenant(e.target.value ? Number(e.target.value) : ""); setPage(0); }}
          style={{
            padding: "8px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0",
            fontSize: 13, color: "#1e293b", background: "white", cursor: "pointer",
          }}
        >
          <option value="">All workspaces</option>
          {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {(filterAction || filterTenant) && (
          <button onClick={resetFilters} style={{
            background: "none", border: "1px solid #e2e8f0", borderRadius: 8,
            padding: "7px 12px", fontSize: 12, cursor: "pointer", color: "#64748b",
          }}>✕ Clear</button>
        )}
        <span style={{ marginLeft: "auto", fontSize: 13, color: "#94a3b8" }}>
          {total.toLocaleString()} event{total !== 1 ? "s" : ""}
        </span>
      </div>

      {isLoading && <p style={{ color: "#64748b" }}>Loading…</p>}

      {/* Table */}
      {!isLoading && logs.length > 0 && (
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
          {logs.map((l, i) => {
            const isOpen = expanded === l.id;
            const color  = actionColor(l.action);
            return (
              <div key={l.id} style={{ borderBottom: i < logs.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                <div
                  onClick={() => setExpanded(isOpen ? null : l.id)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "140px 1fr 160px 130px 24px",
                    gap: 0, padding: "11px 18px", alignItems: "center",
                    cursor: l.details ? "pointer" : "default",
                    background: isOpen ? "#f8fafc" : "white",
                  }}
                >
                  {/* Timestamp */}
                  <div style={{ fontSize: 12, color: "#94a3b8", fontVariantNumeric: "tabular-nums" }}>
                    {formatDateTime(l.created_at)}
                  </div>

                  {/* Action + context */}
                  <div>
                    <span style={{
                      display: "inline-block",
                      background: color + "18", color, border: `1px solid ${color}44`,
                      padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                      marginRight: 8,
                    }}>{l.action}</span>
                    {l.entity_type && (
                      <span style={{ fontSize: 12, color: "#64748b" }}>
                        {l.entity_type}{l.entity_id ? ` #${l.entity_id}` : ""}
                      </span>
                    )}
                  </div>

                  {/* User */}
                  <div style={{ fontSize: 12, color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {l.user_email || "—"}
                  </div>

                  {/* Workspace */}
                  <div style={{ fontSize: 12 }}>
                    <span style={{
                      background: l.tenant_name === "Platform" ? "#f1f5f9" : "#eff6ff",
                      color:      l.tenant_name === "Platform" ? "#64748b" : NAVY,
                      padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                    }}>{l.tenant_name}</span>
                  </div>

                  {/* Expand chevron */}
                  <div style={{ fontSize: 11, color: "#cbd5e1", textAlign: "right" }}>
                    {l.details ? (isOpen ? "▲" : "▼") : ""}
                  </div>
                </div>

                {/* Expanded details */}
                {isOpen && l.details && (
                  <div style={{ padding: "10px 18px 14px", background: "#f8fafc", borderTop: "1px solid #f1f5f9" }}>
                    <pre style={{
                      margin: 0, fontSize: 12, color: "#334155",
                      background: "white", border: "1px solid #e2e8f0",
                      borderRadius: 8, padding: "10px 14px",
                      overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all",
                    }}>
                      {JSON.stringify(l.details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!isLoading && logs.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
          <div style={{ fontSize: 40 }}>📋</div>
          <div style={{ marginTop: 10 }}>No audit events found.</div>
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 20 }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{
            padding: "7px 16px", borderRadius: 8, border: "1.5px solid #e2e8f0",
            background: "white", cursor: page === 0 ? "not-allowed" : "pointer",
            fontSize: 13, color: page === 0 ? "#cbd5e1" : "#475569",
          }}>← Prev</button>
          <span style={{ padding: "7px 14px", fontSize: 13, color: "#64748b" }}>
            Page {page + 1} of {pages}
          </span>
          <button onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1} style={{
            padding: "7px 16px", borderRadius: 8, border: "1.5px solid #e2e8f0",
            background: "white", cursor: page >= pages - 1 ? "not-allowed" : "pointer",
            fontSize: 13, color: page >= pages - 1 ? "#cbd5e1" : "#475569",
          }}>Next →</button>
        </div>
      )}
    </>
  );
}

const thStyle: React.CSSProperties = {
  padding: "10px 14px", textAlign: "left", fontWeight: 600, color: "#475569",
  fontSize: 12, borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap",
};
const tdStyle: React.CSSProperties = {
  padding: "12px 14px", verticalAlign: "middle",
};

// ── Main page ─────────────────────────────────────────────────────────────────

type Tab = "tenants" | "requests" | "create" | "aiconfig" | "billing" | "audit" | "integrations";

// ── Integrations tab ──────────────────────────────────────────────────────────

function PlatformIntegrationCard({
  title, icon, configured, redirectUri, redirectLabel, setupSteps,
  fields, onSave, saving, saveLabel,
}: {
  title: string; icon: React.ReactNode; configured: boolean;
  redirectUri?: string; redirectLabel?: string; setupSteps?: React.ReactNode[];
  fields: { label: string; key: string; value: string; onChange: (v: string) => void; saved?: boolean }[];
  onSave: () => void; saving: boolean; saveLabel: string;
}) {
  const cardStyle: React.CSSProperties = {
    background: "white", borderRadius: 12, padding: "24px 28px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: 20,
  };
  const labelStyle: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6, display: "block" };
  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "9px 12px", borderRadius: 7, border: "1px solid #cbd5e1",
    fontSize: 14, boxSizing: "border-box", fontFamily: "monospace",
  };
  const codeStyle: React.CSSProperties = {
    background: "#f1f5f9", borderRadius: 5, padding: "2px 7px", fontSize: 12,
    fontFamily: "monospace", color: "#0f172a",
  };
  const anyFilled = fields.some(f => f.value);
  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
        {icon}
        <div>
          <div style={{ fontWeight: 700, color: NAVY, fontSize: 16 }}>{title}</div>
          <div style={{ fontSize: 13, color: configured ? "#16a34a" : "#f59e0b", fontWeight: 600 }}>
            {configured ? "✓ Configured" : "⚠ Not configured"}
          </div>
        </div>
      </div>
      {setupSteps && (
        <div style={{ background: "#f8fafc", borderRadius: 8, padding: "12px 16px", marginBottom: 18, fontSize: 13, color: "#475569" }}>
          <strong>Setup instructions:</strong>
          <ol style={{ margin: "8px 0 0", paddingLeft: 18, lineHeight: 2 }}>
            {setupSteps.map((s, i) => <li key={i}>{s}</li>)}
            {redirectUri && <li>Add this redirect URI: <code style={codeStyle}>{redirectUri}</code></li>}
            <li>Paste credentials below</li>
          </ol>
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {fields.map(f => (
          <div key={f.key}>
            <label style={labelStyle}>
              {f.label}{" "}
              {f.saved && <span style={{ fontWeight: 400, color: "#16a34a" }}>(saved — enter new value to replace)</span>}
            </label>
            <input
              style={inputStyle}
              type={f.key.includes("secret") || f.key.includes("token") ? "password" : "text"}
              value={f.value}
              onChange={e => f.onChange(e.target.value)}
              placeholder={f.saved ? "••••••••••••••••" : `Enter ${f.label}`}
            />
          </div>
        ))}
        <button
          onClick={onSave}
          disabled={saving || !anyFilled}
          style={{
            background: NAVY, color: "white", border: "none", padding: "10px 22px",
            borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 14,
            opacity: saving || !anyFilled ? 0.6 : 1, alignSelf: "flex-start",
          }}
        >{saving ? "Saving…" : saveLabel}</button>
      </div>
    </div>
  );
}

function IntegrationsTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: liConfig } = useQuery({ queryKey: ["platform-li-config"], queryFn: getPlatformLinkedInConfig });

  const [liId, setLiId]   = useState("");
  const [liSec, setLiSec] = useState("");

  React.useEffect(() => { if (liConfig) setLiId((liConfig as any).client_id || ""); }, [liConfig]);

  const liMut = useMutation({
    mutationFn: () => savePlatformLinkedInConfig({ ...(liId ? { client_id: liId } : {}), ...(liSec ? { client_secret: liSec } : {}) }),
    onSuccess: () => { toast("LinkedIn config saved", "success"); setLiSec(""); qc.invalidateQueries({ queryKey: ["platform-li-config"] }); },
    onError: () => toast("Failed to save", "error"),
  });

  return (
    <div style={{ maxWidth: 640 }}>
      <LoadingOverlay active={liMut.isPending} />
      <h2 style={{ color: NAVY, fontSize: 18, marginBottom: 20 }}>Platform Integrations</h2>
      <p style={{ fontSize: 13, color: "#64748b", marginBottom: 24 }}>
        Configure OAuth app credentials here once. All tenants then connect their own accounts via Settings → Social Media.
        Posts include a branded job card image (1080×1080) with company logo, job title, location, and jobs email.
      </p>

      {/* LinkedIn */}
      <PlatformIntegrationCard
        title="LinkedIn OAuth App"
        icon={<svg width="28" height="28" viewBox="0 0 24 24" fill="#0a66c2"><path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/></svg>}
        configured={!!(liConfig as any)?.client_id && !!(liConfig as any)?.has_secret}
        redirectUri={(liConfig as any)?.redirect_uri}
        setupSteps={[
          <span>Go to <a href="https://www.linkedin.com/developers/apps/new" target="_blank" rel="noreferrer" style={{ color: "#0a66c2" }}>linkedin.com/developers/apps/new</a></span>,
          <span>Under <strong>Products</strong>, request <strong>"Share on LinkedIn"</strong> and <strong>"Sign In with LinkedIn using OpenID Connect"</strong></span>,
          <span>Under <strong>Auth</strong>, add the redirect URI below</span>,
        ]}
        fields={[
          { label: "Client ID", key: "client_id", value: liId, onChange: setLiId },
          { label: "Client Secret", key: "client_secret", value: liSec, onChange: setLiSec, saved: !!(liConfig as any)?.has_secret },
        ]}
        onSave={() => liMut.mutate()}
        saving={liMut.isPending}
        saveLabel="Save LinkedIn Config"
      />

    </div>
  );
}

export default function SuperAdminPage({ onEnterTenant }: { onEnterTenant: (t: { id: number; name: string }) => void }) {
  const [tab, setTab] = useState<Tab>("tenants");
  const { data: tenants = [] } = useQuery<Tenant[]>({
    queryKey: ["all-tenants"], queryFn: getAllTenants,
  });

  return (
    <div>
      {/* The house `.page-header` block — same title treatment as every tenant
          page, rather than a navy one-off. */}
      <div className="page-header">
        <div>
          <h1>Platform Admin</h1>
        </div>
      </div>

      <div className="tab-pill-bar" style={{ marginBottom: 20 }}>
        <button className={tab === "tenants" ? "active" : ""} onClick={() => setTab("tenants")}>🏢 Tenants</button>
        <button className={tab === "requests" ? "active" : ""} onClick={() => setTab("requests")}>📬 Requests</button>
        <button className={tab === "create" ? "active" : ""} onClick={() => setTab("create")}>➕ Create</button>
        <button className={tab === "aiconfig" ? "active" : ""} onClick={() => setTab("aiconfig")}>🤖 AI Config</button>
        <button className={tab === "billing" ? "active" : ""} onClick={() => setTab("billing")}>💳 Billing</button>
        <button className={tab === "integrations" ? "active" : ""} onClick={() => setTab("integrations")}>🔗 Integrations</button>
        <button className={tab === "audit" ? "active" : ""} onClick={() => setTab("audit")}>📋 Audit Log</button>
      </div>

      {tab === "tenants"      && <TenantsTab onEnterTenant={onEnterTenant} />}
      {tab === "requests"     && <RequestsTab />}
      {tab === "create"       && <CreateTab />}
      {tab === "aiconfig"     && <AiConfigTab />}
      {tab === "billing"      && <BillingTab />}
      {tab === "integrations" && <IntegrationsTab />}
      {tab === "audit"        && <AuditTab tenants={tenants.map(t => ({ id: t.id, name: t.name }))} />}
    </div>
  );
}
